/**
 * Desktop route navigation for the planner and connected workspace screens.
 *
 * Opaque navigation surfaces avoid backdrop sampling during every scroll and interaction.
 * See docs/developer/ui-latency.md for the measured rendering tradeoff.
 *
 * @module apps/web/src/components/layout/Sidebar
 */

import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import { OCRModal } from '@/components/ocr/OCRModal';
import { openNewActivityFlow } from '@/components/activities/NewActivityFlow';
import { cn } from '@/lib/utils';
import { editorToolsApi } from '@/lib/editorToolsApi';
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  LayoutGrid,
  Table,
  Zap,
  Image,
  TrendingUp,
  Settings,
  PlusCircle,
  Activity,
  Layers,
  ClipboardList,
  UsersRound,
  CircleHelp,
  Sparkles,
  Building2,
  Gift,
  Heart,
  PenLine,
} from 'lucide-react';

/** Primary planner-view destinations with their icons and displayed keyboard shortcuts. */
const navItems = [
  { to: '/day', label: 'Day View', icon: Calendar, shortcut: 'Alt+1' },
  { to: '/week', label: 'Week View', icon: CalendarDays, shortcut: 'Alt+2' },
  { to: '/month', label: 'Month View', icon: CalendarRange, shortcut: 'Alt+3' },
  { to: '/kanban', label: 'Kanban', icon: LayoutGrid, shortcut: 'Alt+4' },
  { to: '/table', label: 'Table', icon: Table, shortcut: 'Alt+5' },
  { to: '/scissor', label: 'Scissor Split', icon: Zap, shortcut: 'Alt+6' },
];

/** Secondary workspace, collaboration, state, analytics, invite, help, and settings destinations. */
const toolItems = [
  { to: '/agent', label: 'Flux Agent', icon: Sparkles },
  { to: '/unscheduled', label: 'Unscheduled', icon: ClipboardList },
  { to: '/workspace', label: 'Workspace', icon: Layers },
  { to: '/values', label: 'Values', icon: Heart },
  { to: '/teams', label: 'Teams / ICS', icon: UsersRound },
  { to: '/organization', label: 'Organization', icon: Building2 },
  { to: '/state', label: 'State', icon: Activity },
  { to: '/analytics', label: 'Analytics', icon: TrendingUp },
  { to: '/invite', label: 'Invite friends', icon: Gift },
  { to: '/help', label: 'Help', icon: CircleHelp },
  { to: '/settings', label: 'Settings', icon: Settings },
];

/**
 * Desktop route navigation for the planner and connected workspace screens.
 */
export function Sidebar() {
  const [ocrModalOpen, setOcrModalOpen] = useState(false);
  const [editorAccess, setEditorAccess] = useState(false);
  const ghostBlocks = useScheduleStore(s => s.ghostBlocks);
  const { tasks } = useTasksStore();

  const unscheduledTasks = useMemo(
    () =>
      tasks.filter(
        t =>
          !t.scheduledStart &&
          !t.scheduledEnd &&
          t.status !== 'done' &&
          t.status !== 'completed' &&
          t.status !== 'cancelled'
      ),
    [tasks]
  );
  const unscheduledPreview = unscheduledTasks.slice(0, 40);

  // Editor tools appear only for Flux Agent editors and trainers; the server decides.
  useEffect(() => {
    let active = true;
    void editorToolsApi.status().then(result => {
      if (active) setEditorAccess(Boolean(result.data?.editor || result.data?.admin));
    });
    return () => {
      active = false;
    };
  }, []);
  const visibleToolItems = editorAccess
    ? [...toolItems.slice(0, 1), { to: '/editor-tools', label: 'Editor tools', icon: PenLine }, ...toolItems.slice(1)]
    : toolItems;

  return (
    <>
      <aside className="hidden w-64 flex-col border-r bg-background shadow-sm md:flex">
        <div className="p-4">
          <button
            onClick={() => openNewActivityFlow('choose')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1"
          >
            <PlusCircle className="h-4 w-4" />
            New
          </button>
        </div>
        <nav className="flex-1 space-y-1 p-4 pt-0">
          <div className="mb-6">
            <h3 className="text-muted-foreground mb-2 px-3 text-xs font-semibold uppercase tracking-wider">
              Views
            </h3>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                <span className="text-xs opacity-60">{item.shortcut}</span>
              </NavLink>
            ))}
          </div>

          <div>
            <h3 className="text-muted-foreground mb-2 px-3 text-xs font-semibold uppercase tracking-wider">
              Tools
            </h3>
            <button
              onClick={() => setOcrModalOpen(true)}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <Image className="h-4 w-4" />
              <span>OCR Scan</span>
            </button>
            {visibleToolItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="mt-6">
            <h3 className="text-muted-foreground mb-2 flex items-center justify-between px-3 text-xs font-semibold uppercase tracking-wider">
              <NavLink to="/unscheduled" className="hover:text-foreground">
                Unscheduled
              </NavLink>
              <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[10px]">
                {unscheduledTasks.length}
              </span>
            </h3>
            <div className="max-h-48 space-y-1 overflow-y-auto px-2">
              {unscheduledTasks.length === 0 ? (
                <p className="text-muted-foreground px-2 text-xs italic">All caught up!</p>
              ) : (
                <>
                  {unscheduledPreview.map(task => (
                    <div
                      key={task.id}
                    className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs transition-colors"
                    >
                      <span className="truncate">{task.title}</span>
                      {task.estimatedDuration && (
                        <span className="flex-shrink-0 text-[10px] opacity-60">
                          {task.estimatedDuration}m
                        </span>
                      )}
                    </div>
                  ))}
                  {unscheduledTasks.length > unscheduledPreview.length && (
                    <NavLink
                      to="/unscheduled"
                      className="text-muted-foreground hover:bg-accent hover:text-accent-foreground block rounded-lg px-2 py-1.5 text-xs transition-colors"
                    >
                      View {unscheduledTasks.length - unscheduledPreview.length} more
                    </NavLink>
                  )}
                </>
              )}
            </div>
          </div>
        </nav>

        {ghostBlocks.length > 0 && (
          <div className="border-t p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Zap className="h-4 w-4 text-yellow-500" />
              <span>{ghostBlocks.length} AI Suggestions</span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">Click on ghost blocks to schedule</p>
          </div>
        )}
      </aside>
      <OCRModal open={ocrModalOpen} onClose={() => setOcrModalOpen(false)} />
    </>
  );
}

/**
 * Compact mobile navigation dock using the same application route destinations and active-route
 * styling.
 */
export function MobileDock() {
  const primaryItems = [
    navItems[0],
    navItems[1],
    navItems[3],
    toolItems.find(item => item.to === '/unscheduled'),
    toolItems.find(item => item.to === '/teams'),
    toolItems.find(item => item.to === '/agent'),
  ].filter((item): item is (typeof navItems)[number] | (typeof toolItems)[number] => Boolean(item));

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-background px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] pt-2 shadow-[0_-16px_40px_rgba(0,0,0,0.18)] md:hidden">
        <div className="mx-auto grid max-w-md grid-cols-7 items-center gap-1">
          {primaryItems.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                aria-label={item.label}
                className={({ isActive }) =>
                  cn(
                    'flex h-12 flex-col items-center justify-center gap-0.5 rounded-md text-[10px] font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span className="max-w-full truncate">
                  {item.label.replace(' View', '').replace('Settings', 'Set').replace('Flux Agent', 'Agent')}
                </span>
              </NavLink>
            );
          })}
          <button
            onClick={() => openNewActivityFlow('choose')}
            aria-label="New"
            className="flex h-12 flex-col items-center justify-center gap-0.5 rounded-md bg-primary text-[10px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            <PlusCircle className="h-4 w-4" />
            <span>New</span>
          </button>
        </div>
      </nav>
    </>
  );
}
